import { ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { usePUR2010_R01Store } from "./Store_PUR2010_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultPUR2010_R01HeaderDto, PUR2010_R01HeaderDto } from "./Dto_PUR2010_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { cellDateComparator } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'PUR2010_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<PUR2010_R01HeaderDto> | null>;
	defaultPUR2010_R01HeaderRow: PUR2010_R01HeaderDto;
	handleCreateClick: () => void;
}

export const Table_PUR2010_R01 = (props: Props) => {

	const { gridRef, defaultPUR2010_R01HeaderRow, handleCreateClick } = props;

	const { PUR2010_R01List, 
		selectedPUR2010_R01, setSelectedPUR2010_R01, initSelectedPUR2010_R01,
		columnState, setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = usePUR2010_R01Store();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'purchaseBillNo',
		gridData: PUR2010_R01List,
		defaultGridData: defaultPUR2010_R01HeaderRow,
		selectedRow: selectedPUR2010_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '사업장',
		purchaseDt: '매입일자',
		customNm: '상호',
		purchaseAmt: '매입금액',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compNm')(params), []);

	const baseColumnDefs: ColDef<PUR2010_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 119,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매입일자', colId: 'purchaseDt', field: 'purchaseDt', width: 107,
			cellStyle: (p) => getCellStyle(p, 'purchaseDt'),
			valueFormatter: (p: { data?: PUR2010_R01HeaderDto }) =>
				p.data?.purchaseDt ? formatDate(new Date(p.data.purchaseDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '상호', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '매입금액', colId: 'purchaseAmt', field: 'purchaseAmt', width: 118,
			cellStyle: (p) => getCellStyle(p, 'purchaseAmt'),
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (p) => p.value?.toLocaleString() ?? '',
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<PUR2010_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PUR2010_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);
	

	
	const handleRowClick = useCallback((e: RowClickedEvent<PUR2010_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 계좌와 같은지 확인
		const isSelected = selectedPUR2010_R01?.purchaseBillNo === rowData.purchaseBillNo;
		
		if (isSelected) {
			setSelectedPUR2010_R01(undefined);
			handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedPUR2010_R01(rowData);
		}
	}, [selectedPUR2010_R01, setSelectedPUR2010_R01, setDetailScrollTop, handleCreateClick]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compNm: PUR2010_R01List.length.toLocaleString() + "건",
		purchaseAmt: PUR2010_R01List.reduce((sum, item) => (sum + item.purchaseAmt), 0).toLocaleString(),
		editable: false,
	}]), [PUR2010_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [PUR2010_R01List, selectedPUR2010_R01]);

	return (
		
		<GridContextWrapper<PUR2010_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedPUR2010_R01}
			defaultRowData={defaultPUR2010_R01HeaderRow}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<PUR2010_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<PUR2010_R01HeaderDto>({
					idKey: 'purchaseBillNo',
					selectedList: selectedPUR2010_R01 ? [selectedPUR2010_R01.purchaseBillNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}    

				// Grid_Select에 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
